<template>
  <base-section background-style="light">
    <motion.div
      class="projects-header"
      :initial="{ opacity: 0, y: 30 }"
      :whileInView="{ opacity: 1, y: 0 }"
      :transition="{ duration: 0.6 }"
      :inViewOptions="{ once: true }"
    >
      <gradient-title text="Ausgewählte Projekte" tag="h2" />
      <p class="projects-subtitle">Ein Einblick in aktuelle Webanwendungen und Plattformen</p>
    </motion.div>

    <div class="projects-grid">
      <motion.article
        v-for="(project, index) in projects"
        :key="project.title"
        class="project-card"
        :initial="{ opacity: 0, y: 30 }"
        :whileInView="{ opacity: 1, y: 0 }"
        :transition="{ delay: index * 0.2, duration: 0.6 }"
        :inViewOptions="{ once: true }"
      >
        <div class="project-image-wrapper">
          <img
            :src="require(`@/assets/${project.image}`)"
            :alt="project.imageAlt"
            class="project-image"
          />
        </div>

        <div class="project-content">
          <div class="project-card-header">
            <span class="project-category">{{ project.category }}</span>
            <h3 class="project-title">{{ project.title }}</h3>
          </div>

          <p class="project-description">{{ project.description }}</p>

          <div class="project-tags">
            <span
              v-for="tag in project.tags"
              :key="tag"
              class="project-tag"
            >
              {{ tag }}
            </span>
          </div>

          <div class="project-action">
            <a
              :href="project.link"
              target="_blank"
              rel="noopener"
              class="project-link"
              :aria-label="`Website besuchen: ${project.title}`"
            >
              <span>{{ project.linkText }}</span>
              <svg class="external-icon" viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
            </a>
          </div>
        </div>
      </motion.article>
    </div>
  </base-section>
</template>

<script lang="ts" setup>
import { motion } from "motion-v"
import BaseSection from "@/views/sections/BaseSection.vue"
import GradientTitle from "@/components/GradientTitle.vue"

interface ProjectItem {
  title: string
  category: string
  description: string
  tags: string[]
  link: string
  linkText: string
  image: string
  imageAlt: string
}

const projects: ProjectItem[] = [
  {
    title: 'Bauzaunex',
    category: 'B2B-Bestellportal & E-Commerce System',
    description: 'Ganzheitliche Entwicklung eines High-Performance B2B-Bestellportals für Mobilzäune und Absperrungen. Inklusive 60-Sekunden Express-Besteller, automatisierter Frachtkostenberechnung (Google Distance Matrix API) und 100 % organischer SEO-Strategie.',
    tags: ['Nuxt 4', 'Nitro Engine', 'Vue 3', 'Pinia', 'TypeScript', 'Docker', 'Kubernetes'],
    link: 'https://bauzaun.hamburg/',
    linkText: 'Bauzaun Hamburg – Mobilzaun B2B Portal besuchen',
    image: 'bauzaunex-preview.png',
    imageAlt: 'Bauzaunex B2B-Bestellportal Vorschau'
  },
  {
    title: 'Hottexo Germany',
    category: 'B2B Plattform & Digitales Produkt',
    description: 'Konzeption, UI/UX Design und Entwicklung einer modernen B2B-Homepage für selbsterhitzende Verpflegungslösungen. Optimiert für hohe Conversion-Raten und schnelle Ladezeiten.',
    tags: ['Nuxt 4', 'Nuxt UI', 'Tailwind CSS', 'TypeScript', 'GitOps'],
    link: 'https://selbsterhitzendes-essen.de/',
    linkText: 'Hottexo Germany besuchen',
    image: 'hottexo-preview.jpg',
    imageAlt: 'Hottexo Germany Homepage Vorschau'
  }
]
</script>

<style scoped>
.projects-header {
  text-align: center;
  margin-bottom: 4rem;
}

.projects-subtitle {
  font-size: 1.2rem;
  color: #4a5568;
  max-width: 600px;
  margin: 0 auto;
}

.projects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 2.5rem;
}

.project-card {
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.project-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
}

.project-image-wrapper {
  width: 100%;
  height: 240px;
  overflow: hidden;
  position: relative;
  background-color: #f8fafc;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.project-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  transition: transform 0.4s ease;
}

.project-card:hover .project-image {
  transform: scale(1.03);
}

.project-content {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.project-card-header {
  margin-bottom: 1rem;
}

.project-category {
  display: inline-block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #00d1b2;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.4rem;
}

.project-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a202c;
  line-height: 1.3;
}

.project-description {
  font-size: 0.95rem;
  line-height: 1.6;
  color: #4a5568;
  margin-bottom: 1.5rem;
}

.project-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.8rem;
}

.project-tag {
  background-color: #f1f5f9;
  color: #334155;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
}

.project-action {
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid #f1f5f9;
}

.project-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #00d1b2;
  font-weight: 600;
  text-decoration: none;
  font-size: 0.95rem;
  transition: color 0.2s ease, transform 0.2s ease;
}

.project-link:hover {
  color: #00b398;
  transform: translateX(2px);
}

.external-icon {
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .projects-subtitle {
    font-size: 1rem;
  }

  .projects-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .project-image-wrapper {
    height: 200px;
  }

  .project-content {
    padding: 1.5rem;
  }
}
</style>
